import { computed, reactive, type ComputedRef } from 'vue';
import mime from 'mime';
import { BlobModel } from '@/models/blob';
import { FormFieldType, type FileConfig, type FileLimitKey, type FileLimitOptions, type FormField } from '@/models/form';
import { awaitsDerivative, pollForDerivative } from '@/utils/derivativePoll';
import { generateHexIdentifier } from '@/utils';

/** Where a file belongs: a file field, or one cell of a matrix's file column. */
export type FileSlot = { fieldId: string; rowId?: undefined; columnId?: undefined } |
  { fieldId: string; rowId: string; columnId: string };

/**
 * Stored means the server confirmed the bytes; only a stored file is ever in an answer. Removed is a stored file the
 * reader deleted: it stays listed, out of the answer, until an undo puts it back or the save that drops it (ENG-9096).
 */
export type FileState = 'queued' | 'uploading' | 'stored' | 'failed' | 'removed';

export interface SlotFile {
  /** Given at pick, so a tile keeps its key from queued to stored, whatever the file is called. */
  localId: string;
  slot: FileSlot;
  state: FileState;
  /** 0 to 100 while uploading. */
  progress: number;
  error?: unknown;
  /** The picked file's name, type and size until it is stored, then the blob the server returned. */
  blob: BlobModel;
}

/** A slot's limits as bare values: a count, a size in MB, and a comma-separated extension list. */
export interface FileLimits {
  maximumFileNumber?: number;
  maximumSize?: number;
  allowedExtensions?: string;
}

/** A file the module would not queue, or a removed file it would not put back, by name, and the limit it broke. */
export interface RefusedFile {
  name: string;
  limit: FileLimitKey;
}

/**
 * The port an upload goes through. The S3 adapter serves an entry and the in-memory one the form
 * preview and the tests; the entry picks one when it opens (ENG-9088 decision 5).
 */
export interface Uploader {
  /** Resolves once the server holds the bytes, and rejects on a failure or once `signal` aborts. */
  upload(slot: FileSlot, file: File, onProgress: (percent: number) => void, signal: AbortSignal): Promise<BlobModel>;
  /** Left out by an adapter whose files have no metadata to read and no derivative coming. */
  readMetadata?(identifier: string): Promise<BlobModel>;
  download(identifier: string, fileName: string): void;
  /** Lets go of whatever the adapter holds for a file the module has dropped. */
  release?(identifier: string): void;
}

/** How many files upload at once, across the whole entry (ENG-9088 decision 3). */
export const UPLOADS_AT_ONCE = 3;

const MB = 1048576;

/** A slot as one string: the field id alone for a file field, so a field's key is its id. */
export const slotKey = (slot: FileSlot) => slot.rowId === undefined ?
  slot.fieldId :
  `${slot.fieldId}\u0000${slot.rowId}\u0000${slot.columnId}`;

/** A limit the builder saved as a toggle or the API sent bare, as the value it sets, if it is on. */
const limitValue = (limit: FileConfig | string | number | undefined) => {
  if (limit == null || limit === '') {
    return undefined;
  }
  if (typeof limit === 'object') {
    return limit.enabled === false || limit.value == null || limit.value === '' ? undefined : limit.value;
  }
  return limit;
};

/** A file field's or file column's options, read as the limits `add` takes. */
export const fileLimitsOf = (options: FileLimitOptions | undefined): FileLimits => {
  const count = Number(limitValue(options?.maximumFileNumber));
  const size = Number(limitValue(options?.maximumSize));
  const extensions = limitValue(options?.allowedExtensions);
  return {
    maximumFileNumber: Number.isFinite(count) && count > 0 ? count : undefined,
    maximumSize: Number.isFinite(size) && size > 0 ? size : undefined,
    allowedExtensions: extensions ? `${extensions}` : undefined
  };
};

/** Whether any of the three limits is set. */
export const setsAnyLimit = (limits: FileLimits) => Object.values(limits).some(limit => limit !== undefined);

const extensionOf = (name: string) => {
  const dot = name.lastIndexOf('.');
  return dot < 0 ? '' : name.slice(dot + 1).toLowerCase();
};

/**
 * Whether the list lets the file in, by its extension or by the type the extension names, so a
 * list saying `jpg` takes a `.jpeg`. The list is the builder's text: `pdf, .png`.
 */
const extensionAllowed = (file: File, allowedExtensions: string) => {
  const allowed = allowedExtensions.split(',').map(extension => extension.trim().replace(/^\./, '').toLowerCase())
    .filter(Boolean);
  if (allowed.length === 0) {
    return true;
  }
  const extension = extensionOf(file.name);
  if (allowed.includes(extension)) {
    return true;
  }
  const type = mime.getType(file.name);
  return !!type && allowed.some(allowedExtension => mime.getType(allowedExtension) === type);
};

/** A blob the client made up before the server has answered for it, so it carries no `storedAt`. */
const unreadBlob = ({ identifier, name, contentType, size }: Pick<BlobModel, 'identifier' | 'name' | 'contentType' | 'size'>) =>
  // `IBlob` asks for a `storedAt`, and this blob has none to give.
  new BlobModel({ identifier, name, contentType, size, storedAt: undefined as unknown as Date, hasThumbnail: false });

/** A picked file as a blob the tiles can draw: its name, type and size, and no identifier yet. */
export const pickedBlob = (file: File) => Object.assign(
  unreadBlob({ identifier: '', name: file.name, contentType: file.type, size: file.size }),
  { file, progress: 0 }
);

/**
 * A stored file named by an answer, drawn by its id. It loads only while a metadata read is
 * coming, so an adapter with nothing to read never leaves it loading.
 */
const storedBlob = (identifier: string, isLoading: boolean) =>
  Object.assign(unreadBlob({ identifier, name: '', contentType: '', size: 0 }), { isLoading });

/** `AbortSignal.throwIfAborted` by hand, which Safari has only from 15.4. */
export const throwIfAborted = (signal: AbortSignal) => {
  if (signal.aborted) {
    throw new DOMException('The upload was cancelled.', 'AbortError');
  }
};

/** Whether the tile can draw the file itself: by the type the browser gave it, or by its name. */
export const isImage = (file: File) =>
  file.type.startsWith('image/') || !!mime.getType(file.name)?.startsWith('image/');

/**
 * One entry's files: every slot's list, the uploads, the metadata read on open, the thumbnail
 * polling and, through the adapter, the preview's object URLs (ENG-9088 decision 11). The entry
 * store makes one per open and calls `abortAll()` on the last, so an upload started on one entry
 * never writes into the next. It never writes into `form.items`: a caller reports `answer(slot)`.
 */
export const createFileUploads = ({ uploader }: { uploader: Uploader }) => {
  const slots = reactive(new Map<string, SlotFile[]>());
  /** The limits each slot was last given, for Retry to check the count against. */
  const slotLimits = new Map<string, FileLimits>();
  const controllers = new Map<string, AbortController>();
  /** When each queued file joined the queue, so the first picked starts first. */
  const queuedAt = new Map<string, number>();
  let queueCount = 0;
  let settledWaiters: (() => void)[] = [];
  let closed = false;

  const listFor = (slot: FileSlot) => {
    const key = slotKey(slot);
    if (!slots.has(key)) {
      slots.set(key, []);
    }
    return slots.get(key)!;
  };

  const allFiles = computed(() => [...slots.values()].flat());
  const pending: ComputedRef<SlotFile[]> =
    computed(() => allFiles.value.filter(file => file.state === 'queued' || file.state === 'uploading'));
  const failed: ComputedRef<SlotFile[]> = computed(() => allFiles.value.filter(file => file.state === 'failed'));

  const find = (localId: string) => allFiles.value.find(file => file.localId === localId);

  /** The file, if it is in one of `states`; what each action on a file checks before it acts. */
  const findIn = (localId: string, ...states: FileState[]) => {
    const file = find(localId);
    return file && states.includes(file.state) ? file : undefined;
  };

  /** Lets the adapter go of what it holds for a stored or removed file. */
  const release = (file: SlotFile) => {
    if (file.state === 'stored' || file.state === 'removed') {
      uploader.release?.(file.blob.identifier);
    }
  };

  const drop = (file: SlotFile) => {
    const list = listFor(file.slot);
    const index = list.findIndex(held => held.localId === file.localId);
    if (index > -1) {
      list.splice(index, 1);
    }
    queuedAt.delete(file.localId);
    release(file);
  };

  const resolveSettled = () => {
    if (pending.value.length > 0) {
      return;
    }
    const waiters = settledWaiters;
    settledWaiters = [];
    waiters.forEach(resolve => resolve());
  };

  /**
   * Refetches a stored file until the worker's thumbnail lands, and swaps the answer in. Nothing
   * else would notice it arriving, because a file's metadata is read once. Stops once the file
   * leaves its slot or the instance is dropped.
   */
  const watchForDerivative = (file: SlotFile) => {
    const { readMetadata } = uploader;
    if (!readMetadata) {
      return;
    }
    const identifier = file.blob.identifier;
    const holding = () => closed ? undefined : find(file.localId);
    void pollForDerivative({
      fetch: () => readMetadata(identifier),
      stillWanted: () => holding()?.blob.identifier === identifier,
      onAnswer: fresh => {
        const held = holding();
        if (held?.blob.identifier !== identifier) {
          return;
        }
        fresh.localPreviewUrl = held.blob.localPreviewUrl;
        held.blob = fresh;
      }
    });
  };

  /** Takes the server's blob for a stored file, and waits for its thumbnail if one is coming. */
  const takeBlob = (file: SlotFile, blob: BlobModel) => {
    file.blob = blob;
    if (awaitsDerivative(blob)) {
      watchForDerivative(file);
    }
  };

  const start = (file: SlotFile) => {
    const controller = new AbortController();
    controllers.set(file.localId, controller);
    queuedAt.delete(file.localId);
    file.state = 'uploading';
    file.progress = 0;
    const picked = file.blob.file!;
    // Async, so an adapter that throws before it returns a promise fails this file alone rather
    // than stopping `pump` with the rest of the queue still waiting.
    const run = async () => {
      try {
        const blob = await uploader.upload(file.slot, picked, percent => {
          file.progress = percent;
        }, controller.signal);
        // A cancelled file has already left its slot, so what a late answer writes here is drawn nowhere.
        file.progress = 100;
        file.state = 'stored';
        takeBlob(file, blob);
      } catch (error) {
        file.error = error;
        file.state = 'failed';
      }
    };
    void run().finally(() => {
      if (controllers.get(file.localId) === controller) {
        controllers.delete(file.localId);
      }
      pump();
      resolveSettled();
    });
  };

  /** Starts the oldest queued files, across every slot, until as many are uploading as may be. */
  const pump = () => {
    if (closed) {
      return;
    }
    let uploading = allFiles.value.filter(file => file.state === 'uploading').length;
    const queued = allFiles.value.filter(file => file.state === 'queued')
      .sort((one, other) => queuedAt.get(one.localId)! - queuedAt.get(other.localId)!);
    for (const file of queued) {
      if (uploading >= UPLOADS_AT_ONCE) {
        return;
      }
      start(file);
      uploading++;
    }
  };

  const enqueue = (file: SlotFile) => {
    file.state = 'queued';
    file.progress = 0;
    file.error = undefined;
    queuedAt.set(file.localId, queueCount++);
  };

  /** Stored, queued and uploading files take a place; a failed or removed one gives its place back. */
  const placesTaken = (slot: FileSlot) =>
    listFor(slot).filter(file => file.state !== 'failed' && file.state !== 'removed').length;

  /** Whether the slot's count limit leaves no place for a file coming back to it. */
  const isFull = (slot: FileSlot) => {
    const maximum = slotLimits.get(slotKey(slot))?.maximumFileNumber;
    return maximum !== undefined && placesTaken(slot) >= maximum;
  };

  /**
   * Queues the files that pass the slot's limits and returns the rest, each with the limit it
   * broke. When the pick holds more files than there are places, the first in pick order are taken.
   * A dropped instance takes nothing, since nothing would ever upload it.
   */
  const add = (slot: FileSlot, picked: File[], limits: FileLimits): RefusedFile[] => {
    if (closed) {
      return [];
    }
    const key = slotKey(slot);
    slotLimits.set(key, limits);
    const refused: RefusedFile[] = [];
    let places = limits.maximumFileNumber === undefined ?
      Infinity :
      Math.max(limits.maximumFileNumber - placesTaken(slot), 0);
    const list = listFor(slot);
    for (const file of picked) {
      if (limits.allowedExtensions && !extensionAllowed(file, limits.allowedExtensions)) {
        refused.push({ name: file.name, limit: 'allowedExtensions' });
      } else if (limits.maximumSize !== undefined && file.size > limits.maximumSize * MB) {
        refused.push({ name: file.name, limit: 'maximumSize' });
      } else if (places <= 0) {
        refused.push({ name: file.name, limit: 'maximumFileNumber' });
      } else {
        places--;
        list.push({ localId: generateHexIdentifier(16), slot, state: 'queued', progress: 0, blob: pickedBlob(file) });
        enqueue(list[list.length - 1]);
      }
    }
    pump();
    return refused;
  };

  /** Uploads a failed file again, unless its slot has filled up since; then it is refused. */
  const retry = (localId: string): RefusedFile | undefined => {
    const file = findIn(localId, 'failed');
    if (closed || !file) {
      return undefined;
    }
    if (isFull(file.slot)) {
      return { name: file.blob.name, limit: 'maximumFileNumber' };
    }
    enqueue(file);
    pump();
    return undefined;
  };

  /** Aborts a queued or uploading file and drops it from its slot. */
  const cancel = (localId: string) => {
    const file = findIn(localId, 'queued', 'uploading');
    if (!file) {
      return;
    }
    controllers.get(localId)?.abort();
    controllers.delete(localId);
    drop(file);
    pump();
    resolveSettled();
  };

  /**
   * Drops a failed file, and marks a stored one removed, which takes it out of the answer and keeps it listed for an
   * undo. Until Save the removal is a local edit, so nothing reaches the server (ENG-9100 decision 9).
   */
  const remove = (localId: string) => {
    const file = findIn(localId, 'failed', 'stored');
    if (file?.state === 'failed') {
      drop(file);
    } else if (file) {
      file.state = 'removed';
    }
  };

  /**
   * Puts a removed file back into the answer, unless its slot has filled up since the removal gave its place back;
   * then it stays removed and is refused, as a Retry is.
   */
  const undo = (localId: string): RefusedFile | undefined => {
    const file = findIn(localId, 'removed');
    if (closed || !file) {
      return undefined;
    }
    if (isFull(file.slot)) {
      return { name: file.blob.name, limit: 'maximumFileNumber' };
    }
    file.state = 'stored';
    return undefined;
  };

  /** Drops every removed file, once the save that removes them has gone through. */
  const dropRemoved = () => {
    allFiles.value.filter(file => file.state === 'removed').forEach(drop);
  };

  /**
   * Drops a stored or removed file outright, for an answer that has let go of it. Nothing announces it and nothing can
   * undo it, since the reader didn't ask for it.
   */
  const letGo = (localId: string) => {
    const file = findIn(localId, 'stored', 'removed');
    if (file) {
      drop(file);
    }
  };

  const files = (slot: FileSlot): SlotFile[] => slots.get(slotKey(slot)) ?? [];

  /** The slot's answer: its stored files' ids, in order, and nothing else. */
  const answer = (slot: FileSlot): string[] =>
    files(slot).filter(file => file.state === 'stored').map(file => file.blob.identifier);

  /** Resolves once nothing is queued or uploading. */
  const settled = () => new Promise<void>(resolve => {
    settledWaiters.push(resolve);
    resolveSettled();
  });

  /**
   * Stops the instance: aborts every queued and uploading file, stops the thumbnail polling and
   * lets the adapter go of what it holds. The store calls it before dropping the instance. Stored
   * files stay listed, so a caller still watching this instance never reads an emptied answer and
   * reports it.
   */
  const abortAll = () => {
    closed = true;
    pending.value.forEach(file => {
      controllers.get(file.localId)?.abort();
      drop(file);
    });
    controllers.clear();
    allFiles.value.forEach(release);
    resolveSettled();
  };

  const readSlot = async (slot: FileSlot, fileIds: string[]) => {
    const list = listFor(slot);
    // A file the slot already holds is not read again, so a second call adds nothing to the answer.
    const held = new Set(answer(slot));
    const { readMetadata } = uploader;
    const seeded = fileIds.filter(identifier => !held.has(identifier)).map(identifier => {
      const blob = storedBlob(identifier, !!readMetadata);
      list.push({ localId: generateHexIdentifier(16), slot, state: 'stored', progress: 100, blob });
      return list[list.length - 1];
    });
    if (!readMetadata) {
      return;
    }
    await Promise.all(seeded.map(async file => {
      try {
        const blob = await readMetadata(file.blob.identifier);
        if (closed || !find(file.localId)) {
          return;
        }
        if (blob) {
          takeBlob(file, blob);
        } else {
          // `BlobService` answers with a blob or throws. This guards an adapter that breaks the port.
          file.blob.isLoading = false;
        }
      } catch {
        // Still stored, and still in the answer: a failed read loses the file's name, not the file.
        file.blob.isLoading = false;
      }
    }));
  };

  /**
   * Seeds every file field's and matrix file cell's slot with the stored files its answer names,
   * then reads their metadata. The answer holds from the start, before a read comes back.
   */
  const readStored = async (items: FormField[]) => {
    const reads: Promise<void>[] = [];
    for (const field of items) {
      if (field.type === FormFieldType.FILE && field.fileIds?.length) {
        reads.push(readSlot({ fieldId: field.identifier }, field.fileIds));
      }
      if (field.type === FormFieldType.MATRIX) {
        for (const row of field.matrixTableRowValues ?? []) {
          for (const column of row.columnValues ?? []) {
            if (column.fileIds?.length) {
              reads.push(readSlot({ fieldId: field.identifier, rowId: row.rowId, columnId: `${column.id}` }, column.fileIds));
            }
          }
        }
      }
    }
    await Promise.all(reads);
  };

  return {
    files,
    answer,
    add,
    retry,
    cancel,
    remove,
    undo,
    letGo,
    dropRemoved,
    pending,
    failed,
    settled,
    abortAll,
    readStored,
    download: (localId: string) => {
      const file = find(localId);
      if (file?.state === 'stored') {
        uploader.download(file.blob.identifier, file.blob.name);
      }
    }
  };
};

export type FileUploads = ReturnType<typeof createFileUploads>;
